import { Suspense, useMemo, useState } from 'react';

import { fetchPostIwocapayPaymentLater } from '@iwoca/lapi-client/edge';
import { fetchPostSharedUserAction } from '@iwoca/lapi-client/iwocapay';
import { useSearchParams } from 'react-router';

import {
  SELLER_GET_ERROR,
  useGetIwocapayBuyerPayLinkPackage,
  useGetProfile,
  useGetSellerByHandle,
} from '../../../api/lending/lapiHooks';
import { LoadingScreen } from '../../../components/LoadingScreen/LoadingScreen';
import { useAccountNavigation } from '../../../hooks/useAccountNavigation';
import { useQueryParam } from '../../../hooks/useQueryParam';
import { useSellerWhitelabelling } from '../../../hooks/useSellerWhiteLabelling.hook';
import { GenericError } from '../../../Pages/FailurePages/GenericError';
import { createBuyerAccount } from '../../../Pages/stateApi';
import { FormalOffer } from '../../../Seller/PayLinks/utils/Paylinks.types';
import { useThreatMetrix } from '../../../utils/ThreatMetrix';
import { useThreatMetrixProfiling } from '../../../utils/threatMetrix/ThreatMetrixProfiling';
import { trackSelectedCheckoutOption } from '../../../utils/tracking';
import { LoginBanner } from '../../components/LoginBanner';
import { PrivacyPolicy } from '../../components/PrivacyPolicy/privacyPolicy';
import { WhiteLabelOverride } from '../../components/WhiteLabelOverride/WhiteLabelOverride';
import {
  TEnabledOptions,
  useEnabledOptions,
} from '../../hooks/useEnabledTerms';
import { useIsOverlay } from '../../hooks/useIsOverlay';
import { useTrackLandingPageView } from '../../hooks/useTrackLandingPageView';
import { LoginPanel } from '../../LoginPanel/LoginPanel';
import {
  payNowCheckoutPayLinkPackage,
  payNowCheckoutUniversalLink,
} from '../../PayNow/checkout';
import { SIGNUP_BASE_PATH } from '../../Signup/routes';
import { createState } from '../../utils/customerState';
import {
  durationFromPricingOption,
  productFromDuration,
} from '../../utils/product';
import { buildQueryString } from '../../utils/queryParams';
import { PayLaterDecisionModal } from '../components/PayLaterDecisionModal/PayLaterDecisionModal';
import { HelpTextAccordion } from '../HelpTextAccordion/HelpTextAccordion';
import { useGetValidIwocapayFormalOffers } from '../hooks/useGetValidIwocapayFormalOffers';
import { usePayLaterDecisionFlow } from '../hooks/usePayLaterDecisionFlow';
import { PaymentDetails } from '../PaymentDetails/PaymentDetails';
import { FormValues } from '../utils/PayLinkLanding.types';
import {
  getBuyerInterest,
  getPricingPromotions,
} from '../utils/productPricingHelpers';
import { redirectAfterPayLater } from '../utils/redirectAfterPayLater';

export const GenericPayLinkLanding = ({
  sellerHandle,
  payLinkPackageId,
}: {
  sellerHandle: string;
  payLinkPackageId?: string;
}) => {
  const { navigateToAccount } = useAccountNavigation();
  const decisionFlow = usePayLaterDecisionFlow();
  const { sellerError, seller, loadingSeller } =
    useGetSellerByHandle(sellerHandle);

  // White-label theme
  useSellerWhitelabelling(sellerHandle);

  const amount = useQueryParam('amount');
  const reference = useQueryParam('reference');

  const { formalOffers, loadingFormalOffers } =
    useGetValidIwocapayFormalOffers();

  const { payLinkPackage, loadingPayLinkPackage } =
    usePayLinkPackage(payLinkPackageId);
  const { enabledOptions, loadingOptions } = useEnabledOptions({
    pricing: payLinkPackage?.product_pricing || seller?.product_pricing,
    allowedOptions: payLinkPackageId
      ? payLinkPackage?.allowed_payment_terms
      : seller?.allowed_payment_terms,
  });

  useThreatMetrixProfiling(sellerHandle);

  const isOverlay = useIsOverlay();

  const queryParamAmount = parseAmountFromQueryParam(amount);

  const {
    profile: { email_address },
  } = useGetProfile();

  // User defined state of the form saved in the URL, back up for page refreshes or navigation
  const [searchParams, setSearchParams] = useSearchParams();
  const getStateFromUrl = () => {
    const encodedUserState = searchParams.get('state');
    const userState = encodedUserState
      ? JSON.parse(atob(encodedUserState))
      : {};
    return userState;
  };

  const setUrlState = (key: string, value: string) => {
    const newUserState = getStateFromUrl();
    if (value) {
      newUserState[key] = value;
    } else {
      delete newUserState[key];
    }
    const encodedUserState = btoa(JSON.stringify(newUserState));
    searchParams.set('state', encodedUserState);

    setSearchParams(searchParams, { replace: true });
  };

  const initialUrlState = useMemo(
    () => getStateFromUrl(),

    // use memo here is needed to stop the form provider loosing context and dropping the selected product.
    // we do need it to recalculate when logging in hence the dep array. otherwise it would revert to the initial values the page loaded with
    // we must use email_address over profile as profile changes in memory a lot but the string comparison is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [email_address],
  );

  const initialValues = {
    amount: (
      payLinkPackage?.amount ||
      queryParamAmount ||
      initialUrlState.amount ||
      ''
    ).toString(),
    reference:
      payLinkPackage?.reference || reference || initialUrlState.reference || '',
  };

  const [loginPanelOpen, setLoginPanelOpen] = useState(false);

  useTrackLandingPageView({
    payLinkType: 'generic',
    visibleOptions: enabledOptions,
    productPricing: payLinkPackage
      ? payLinkPackage.product_pricing
      : seller?.product_pricing,
    sellerId: seller?.seller_id,
    loadingApis: loadingPayLinkPackage || loadingSeller,
  });

  useThreatMetrix();

  if (sellerError) {
    return <GenericError errorText={sellerError as string} />;
  }

  if (!seller || loadingSeller || loadingOptions || loadingFormalOffers) {
    return <LoadingScreen />;
  }

  if (payLinkPackage?.deleted_at) {
    return <GenericError errorText={SELLER_GET_ERROR} />;
  }

  const productPricing = payLinkPackage
    ? payLinkPackage.product_pricing
    : seller.product_pricing;

  const buyerInterest = getBuyerInterest({
    productPricing,
    formalOffers: formalOffers,
  });

  const pricingPromotions = getPricingPromotions({
    productPricing,
  });

  return (
    <Suspense fallback={<LoadingScreen />}>
      <div className="min-h-[100vh] w-full text-left text-m leading-[150%] text-denim-25">
        <div className="mx-auto flex max-w-[1440px] flex-col">
          <div className="m700:grid m700:flex-grow m700:gap-[2rem] m700:[grid-template-areas:'._payment-details_help-accordion'] m700:[grid-template-columns:1fr_2fr] m900:[grid-template-columns:1fr_2fr_1fr]">
            <div className="m700:mt-2xl m700:[grid-area:payment-details]">
              <WhiteLabelOverride componentType="background">
                <LoginBanner onClick={() => setLoginPanelOpen(true)} />
              </WhiteLabelOverride>
              <PaymentDetails
                isPreview={false}
                sellerName={seller.trading_name}
                pricingPromotions={pricingPromotions}
                buyerInterest={buyerInterest}
                onSubmit={onSubmit({
                  sellerHandle,
                  payLinkPackageId,
                  formalOffers,
                  enabledOptions,
                  isOverlay,
                  navigateToAccount,
                  decisionFlow,
                })}
                initialValues={initialValues}
                setUrlState={setUrlState}
                lockAmount={Boolean(payLinkPackage?.amount || queryParamAmount)}
                lockReference={Boolean(payLinkPackage?.reference || reference)}
                enabledOptions={enabledOptions}
                payLinkType="generic"
              />
              <PrivacyPolicy />
            </div>

            <div className="m700:flex m700:flex-col m700:justify-between m700:[grid-area:help-accordion]">
              <HelpTextAccordion
                className="mx-l mb-l mt-0 m700:mt-2xl"
                enabledOptions={enabledOptions}
              />
            </div>
          </div>
        </div>
        <LoginPanel
          open={loginPanelOpen}
          close={() => setLoginPanelOpen(false)}
        />
        <PayLaterDecisionModal status={decisionFlow.status} />
      </div>
    </Suspense>
  );
};

const onSubmit = ({
  sellerHandle,
  payLinkPackageId,
  formalOffers,
  enabledOptions,
  isOverlay,
  navigateToAccount,
  decisionFlow,
}: {
  sellerHandle: string;
  payLinkPackageId?: string;
  formalOffers?: FormalOffer[];
  enabledOptions: TEnabledOptions;
  isOverlay: boolean;
  navigateToAccount: (queryString?: string) => void;
  decisionFlow: ReturnType<typeof usePayLaterDecisionFlow>;
}) => {
  return async ({
    values,
    stateKey,
  }: {
    values: FormValues;
    stateKey?: string;
  }) => {
    const {
      paymentOption,
      reference,
      payNowFullName,
      payNowEmailAddress,
      payNowMarketingConsent: marketingConsent,
    } = values;
    const amount = parseFloat(values.amount);

    if (paymentOption === 'payNow') {
      const customerDetails = {
        emailAddress: payNowEmailAddress,
        fullName: payNowFullName,
      };

      if (payLinkPackageId) {
        return payNowCheckoutPayLinkPackage({
          payLinkPackageId,
          payLinkDetails: {
            amount,
            reference,
          },
          customerDetails,
          marketingConsent,
          isOverlay,
        });
      }

      return payNowCheckoutUniversalLink({
        sellerHandle,
        payLinkDetails: {
          amount,
          reference,
        },
        customerDetails,
        marketingConsent,
        isOverlay,
      });
    } else {
      const selectedOption = values.paymentOption
        ? productFromDuration[durationFromPricingOption[values.paymentOption]!]
        : 'THREE_MONTHS';

      const payLaterRequestBody = {
        data: {
          amount,
          reference,
          seller_handle: sellerHandle,
          pay_link_package_id: payLinkPackageId,
          requested_duration: selectedOption,
        },
      };

      if (stateKey) {
        const payLinkResponse = await fetchPostIwocapayPaymentLater({
          stateKey,
          body: payLaterRequestBody,
        });
        const payLink = await payLinkResponse.json();

        trackSelectedCheckoutOption({
          payLinkId: payLink.data.id as string,
          visibleOptions: enabledOptions,
          selectedOption: selectedOption,
        });

        await fetchPostSharedUserAction({
          stateKey,
          body: { user_action: 'CHECKOUT_PAY_LINK' },
        });

        const queryParams = { payLinkId: payLink.data.id as string };
        redirectAfterPayLater({
          paymentOption,
          payLinkAmount: amount,
          formalOffers,
          navigateToAccount,
          onAwaitDecision: decisionFlow.isEnabled
            ? () =>
                void decisionFlow.trigger({
                  payLinkAmount: amount,
                  queryParams,
                })
            : undefined,
          queryParams,
          extraAccountParams: { source: 'iwocapay-pay-link' },
        });
      } else {
        const { emailAddress, marketingOptIn } = values;
        const customerState = await createBuyerAccount(
          createState(emailAddress, marketingOptIn),
        );

        const payLinkResponse = await fetchPostIwocapayPaymentLater({
          stateKey: customerState.data!.state_key,
          body: payLaterRequestBody,
        });
        const payLink = await payLinkResponse.json();

        trackSelectedCheckoutOption({
          payLinkId: payLink.data.id as string,
          visibleOptions: enabledOptions,
          selectedOption: selectedOption,
        });

        await fetchPostSharedUserAction({
          stateKey: customerState.data!.state_key,
          body: { user_action: 'CHECKOUT_PAY_LINK' },
        });

        window.location.href = SIGNUP_BASE_PATH + buildQueryString();
      }
    }
  };
};

function usePayLinkPackage(payLinkPackageId?: string) {
  const { iwocapayBuyerPayLinkPackage: payLinkPackage, loading } =
    useGetIwocapayBuyerPayLinkPackage({
      payLinkPackageId,
      enabled: !!payLinkPackageId,
    });

  return {
    payLinkPackage,
    loadingPayLinkPackage: loading,
  };
}

function parseAmountFromQueryParam(amount: string | null) {
  if (!amount) return;

  const sanitisedString = amount.replace(',', '');
  const parsedAmount = parseFloat(sanitisedString);
  if (isNaN(parsedAmount)) return;

  return parsedAmount.toFixed(2);
}
